<template>
  <!-- Only render if a flow exists -->
  <div v-if="flowStore.currentFlow">
    <!-- No zones fallback -->
    <div
      v-if="!isLoading && Object.keys(zoneTotalCounts).length === 0"
      class="no-zones-container"
    >
      <p>{{ $t('explore.counters.noZones') }}</p>
    </div>

    <!-- Section: Visits — polygon zone_in count, one big number per zone.
         Combined EE lives in the directional row regardless of geometry. -->
    <section v-if="polygonTotalZones.length" class="kpi-section tone-visits">
      <h6 class="section-head">
        <i class="bi bi-people section-icon"></i>
        {{ $t('explore.counters.zoneEntries', { period: durationText.toLowerCase() }) }}
      </h6>
      <div class="kpi-grid tight">
        <div
          v-for="([zoneName, zoneDetails]) in polygonTotalZones"
          :key="`total-${zoneName}`"
          class="kpi-card kpi-accent"
          :style="{ '--accent': getColorForZone(zoneDetails.zone_type) }"
        >
          <div class="kpi-head">
            <p class="kpi-title">
              <b :style="{ color: getColorForZone(zoneDetails.zone_type) }">{{ zoneName }}</b>
            </p>
            <span class="kpi-sub">{{ zoneTypeLabel(zoneDetails.zone_type) }}</span>
          </div>
          <!-- Zone identity 6.6: changed type today — history still counted as the old type. -->
          <div v-if="typeChangedNote(zoneName)" class="kpi-type-note">
            <i class="bi bi-arrow-repeat"></i> {{ typeChangedNote(zoneName) }}
          </div>
          <div class="kpi-value">
            <div v-if="isLoading" class="loader tiny"></div>
            <span v-else class="kpi-number">{{ zoneDetails.zone_in }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Section: Average dwell time — polygon-only, dwell-meaningful zones. -->
    <section v-if="dwellAverageZones.length" class="kpi-section tone-dwell">
      <h6 class="section-head">
        <i class="bi bi-stopwatch section-icon"></i>
        {{ $t('explore.counters.avgDwell', { period: durationText.toLowerCase() }) }}
      </h6>
      <div class="kpi-grid tight">
        <div
          v-for="([zoneName, zoneDetails]) in dwellAverageZones"
          :key="`avg-${zoneName}`"
          class="kpi-card kpi-accent"
          :style="{ '--accent': getColorForZone(zoneDetails.zone_type) }"
        >
          <div class="kpi-head">
            <p class="kpi-title">
              <b :style="{ color: getColorForZone(zoneDetails.zone_type) }">{{ zoneName }}</b>
            </p>
            <span class="kpi-sub">{{ zoneTypeLabel(zoneDetails.zone_type) }}</span>
          </div>
          <!-- Zone identity 6.6: changed type today — history still counted as the old type. -->
          <div v-if="typeChangedNote(zoneName)" class="kpi-type-note">
            <i class="bi bi-arrow-repeat"></i> {{ typeChangedNote(zoneName) }}
          </div>
          <div class="kpi-value">
            <div v-if="isLoading" class="loader tiny"></div>
            <span v-else class="kpi-number">{{ formattedAverageDurationTime(zoneDetails.average_duration) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Section: Crossings — directional in/out pills. Combined EE always here;
         other zones appear when they carry a tripwire. -->
    <section v-if="directionalTotalZones.length" class="kpi-section tone-crossings">
      <h6 class="section-head">
        <i class="bi bi-arrow-left-right section-icon"></i>
        {{ $t('explore.counters.crossings', { period: durationText.toLowerCase() }) }}
      </h6>
      <div class="kpi-grid tight">
        <div
          v-for="([zoneName, zoneDetails]) in directionalTotalZones"
          :key="`dir-${zoneName}`"
          class="kpi-card kpi-accent"
          :style="{ '--accent': getColorForZone(zoneDetails.zone_type) }"
        >
          <div class="kpi-head">
            <p class="kpi-title">
              <b :style="{ color: getColorForZone(zoneDetails.zone_type) }">{{ zoneName }}</b>
              <i
                v-if="zoneHasTripwireByName(zoneName)"
                class="bi bi-arrow-left-right kpi-glyph"
                :title="zoneHasPolygonByName(zoneName) ? $t('explore.counters.polygonTripwire') : $t('explore.counters.tripwire')"
              ></i>
            </p>
            <span class="kpi-sub">{{ zoneTypeLabel(zoneDetails.zone_type) }}</span>
          </div>
          <!-- Zone identity 6.6: changed type today — history still counted as the old type. -->
          <div v-if="typeChangedNote(zoneName)" class="kpi-type-note">
            <i class="bi bi-arrow-repeat"></i> {{ typeChangedNote(zoneName) }}
          </div>
          <div class="kpi-duo">
            <div class="kpi-pill">
              <div class="label">{{ inLabel(zoneDetails.zone_type) }}</div>
              <div class="val">
                <div v-if="isLoading" class="loader tiny"></div>
                <b v-else>{{ storeTotalCounts[zoneName]?.zone_in_count || 0 }}</b>
              </div>
            </div>
            <div class="kpi-pill">
              <div class="label">{{ outLabel(zoneDetails.zone_type) }}</div>
              <div class="val">
                <div v-if="isLoading" class="loader tiny"></div>
                <b v-else>{{ storeTotalCounts[zoneName]?.zone_out_count || 0 }}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, toRefs, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatDate } from '@/i18n/format'
import { zoneTypeLabel } from '@/i18n/zoneTypes'
import { useRoute } from 'vue-router'
import { countersForCards, storeCountersByCardName } from '@/zones/countersByKey'
import { useChangesTodayStore } from '@/stores/ChangesToday'
import { NOTICES, ZONE_TYPE_NAMES, typeChangeOf } from '@/zones/changesToday'

import { detection_zone_colors_by_name_solid } from '@/constants.js'

import { useFlowStore } from '@/stores/FlowStore'

import { getCurrentCountersByFlowId, getCountersByDuration, getCountersByDateRange, } from "@/api/eventsAPI.js"

export default {
    name: 'Counters',

    components: {
        
    },

    props: {
        flowId: Number,
        startDate: Object,
        endDate: Object,
        duration: Number,
        isTodaySelected: Boolean,
        dateRangeMode: String,
        openingHoursMode: String,
        // Live polygon-occupancy counts streamed via Socket.IO from the parent
        // (FlowOutput owns the socket connection). { zoneName: number }
        liveCounters: { type: Object, default: () => ({}) },
        // Whether the camera stream is currently running. When false, the card
        // displays "N/A" for the live counter (matches the previous behavior).
        streamStatus: { type: Boolean, default: false },
    },

    setup(props, { emit }) {
        const { t } = useI18n()
        const flowStore = useFlowStore()

        const route = useRoute()

        const detectionZoneNames = ref({})
        const zoneAverageTime = ref({})
        const zoneTotalCounts = ref({})
        const storeTotalCounts = ref({})

        const isLoading = ref(true)
        const isMounted = ref(true)
        let currentFetchController = null
        let latestRequestId = 0

        const { flowId, duration, isTodaySelected, startDate, endDate, openingHoursMode } = toRefs(props)

        const localDateRangeMode = ref(props.dateRangeMode)

        // Zone identity 6.6: a zone that changed type today shows under its new type, but
        // its history (last 7 / 30 days, a date range) was counted as the old one until
        // tonight — the card says so. Today's live counts are already the new type.
        const changesToday = useChangesTodayStore()
        changesToday.ensureLoaded(null)
        const typeChangedNote = (zoneName) => {
            if (isTodaySelected.value) return null
            const z = (detectionZoneNames.value || {})[zoneName] || {}
            const e = typeChangeOf(changesToday.register, { key: z.ZoneKey ?? null, name: zoneName })
            return e ? NOTICES.flowCard(ZONE_TYPE_NAMES[e.from] || 'zone of another type') : null
        }

        let fetchIntervalCounterData = null

        const stopInterval = () => {
            if (fetchIntervalCounterData) {
                clearInterval(fetchIntervalCounterData)
                fetchIntervalCounterData = null
            }
        }

        const startInterval = () => {
            if (isTodaySelected.value) { // Only start interval if isTodaySelected is true
                stopInterval() // Ensure any existing interval is cleared
                fetchIntervalCounterData = setInterval(refreshData, 5000) // Refresh every 5 seconds
            }
        }

        const refreshData = () => {
            if (!isMounted.value) return

            stopInterval(); // Stop the interval before making a new API call

            if (localDateRangeMode.value === 'duration') {
                fetchCountersByDuration()
            } else if (localDateRangeMode.value === 'dateRange') {
                fetchCountersByDateRange()
            }
        }

        const debounce = (func, wait) => {
            let timeOut
            return (...args) => {
                clearTimeout(timeOut)
                timeOut = setTimeout(() => func.apply(this, args), wait)
            }
        }

        const initializeZoneTotalCounts = () => {
            const names = detectionZoneNames.value || {}
            zoneTotalCounts.value = Object.keys(names).reduce((acc, key) => {
                acc[key] = {
                zone_type: names[key].type,
                zone_in: 0
                }
                return acc
            }, {})
        }

        const initializeZoneAverageTime = () => {
            const names = detectionZoneNames.value || {}
            zoneAverageTime.value = Object.keys(names).reduce((acc, key) => {
                acc[key] = {
                zone_type: names[key].type,
                average_duration: 0
                }
                return acc
            }, {})
        }

        onMounted(async() => {
            // if parent never fetched a flow, bail out
            if (!flowStore.currentFlow?.DetectionZones) {
                // nothing to do—parent hasn’t loaded or no zones
                return
            }
            
            isMounted.value = true
            isLoading.value = true
            
            detectionZoneNames.value = flowStore.currentFlow.DetectionZones
            
            initializeZoneTotalCounts()
            initializeZoneAverageTime()
            
            refreshData()  // Fetch data when component is mounted
            
            startInterval() // Set up an interval to keep fetching data
        })
        
        watch(duration, debounce((newValue) => {
            if (newValue) {
                isLoading.value = true
                stopInterval() // Stop the interval before making a new API call
                refreshData()
            }
        }, 300))

        watch(() => props.dateRangeMode, (newValue) => {
            localDateRangeMode.value = newValue
        })

        watch(localDateRangeMode, debounce((newValue) => {
            if (newValue) {
                isLoading.value = true
                stopInterval() // Stop the interval before making a new API call
                refreshData()
            }
        }, 300))

        watch(() => isTodaySelected.value, (newValue) => {
            if (newValue) {
                // Start interval for "Today's Real-Time Data"
                localDateRangeMode.value = 'duration'
                isLoading.value = true
                startInterval()
            } else {
                // Fetch data once for "Historical Data" and stop the interval
                isLoading.value = true
                stopInterval()
            }
            refreshData()
        })

        watch(([startDate, endDate]), debounce ((newValues, prevValues) => {
            if (newValues.every(value => value !== null)) { // Both dates are selected
                isLoading.value = true
                stopInterval() // Stop the interval before making a new API call
                refreshData()
            }
        }, 300))

        watch(() => openingHoursMode.value, debounce((newVal) => {
            if (newVal) {
                isLoading.value = true
                stopInterval()
                refreshData()
            }
        }, 300))
        
        const fetchCountersByDuration = async () => {
            if (currentFetchController && !currentFetchController.signal.aborted) {
                currentFetchController.abort()
            }

            const controller = new AbortController()
            currentFetchController = controller // Update the current controller
            const requestId = ++latestRequestId


            if (flowId.value) {
                try {
                    if (controller.signal.aborted || !isMounted.value) {
                        return  // Abort early if the signal is already aborted
                    }
                    
                    let counts

                    if (isTodaySelected.value) {
                        counts = await getCurrentCountersByFlowId(flowId.value, { signal: controller.signal })
                    } else {
                        counts = await getCountersByDuration(flowId.value, duration.value, openingHoursMode.value, { signal: controller.signal })
                    }

                    if (controller.signal.aborted || !isMounted.value || requestId !== latestRequestId) {
                        return // Abort early if the signal is already aborted or not mounted or not the lastRequestId
                    }

                    processZoneCounts(counts)
                    isLoading.value = false
                    startInterval()
                } catch (error) {
                    isLoading.value = false
                    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                        console.log('Request was canceled')
                    } else {
                        console.error('Error fetching event data:', error)
                    }
                    startInterval() // Restart the interval even if there is an error  
                }
            }
        }

        const fetchCountersByDateRange = async () => {
            if (currentFetchController && !currentFetchController.signal.aborted) {
                currentFetchController.abort()
            }

            const controller = new AbortController()
            currentFetchController = controller // Update the current controller
            const requestId = ++latestRequestId

            if (startDate.value && endDate.value && flowId.value) {
                try {
                    if (controller.signal.aborted || !isMounted.value) {
                        return  // Abort early if the signal is already aborted
                    }

                    let counts = await getCountersByDateRange(flowId.value, startDate.value, endDate.value, openingHoursMode.value, { signal: controller.signal })
                    
                    if (controller.signal.aborted || !isMounted.value || requestId !== latestRequestId){
                        return
                    }

                    processZoneCounts(counts)
                    isLoading.value = false
                } catch (error) {
                    isLoading.value = false
                    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                        console.log('Request was canceled')
                    } else {
                        console.error('Error fetching event data:', error)
                    }
                }
            }
        }

        const processZoneCounts = (counts) => {
            if (!isMounted.value) return

            // Defensive parsing: the analytics endpoint can legitimately return a
            // payload without counters_data (e.g. while the next ETL run is still
            // producing Redis keys), and historically this line had a fallback
            // to an undefined identifier that threw ReferenceError when it fired.
            const counters = counts?.counters_data?.counters || {}
            const storeCounters = counts?.counters_data?.store_counters || null

            // Update zoneAverageTime and zoneTotalCounts — each card's figures found by
            // the zone's key, not its name (option A, phase 3; zones/countersByKey.js).
            const forCards = countersForCards(detectionZoneNames.value, counters)
            for (let key in zoneAverageTime.value) {
                const c = forCards[key]
                if (c) {
                    zoneAverageTime.value[key].average_duration = c.average_duration
                    zoneTotalCounts.value[key].zone_in = c.zone_in_count
                    zoneTotalCounts.value[key].zone_type = c.zone_type
                } else {
                    zoneAverageTime.value[key].average_duration = 0
                    zoneTotalCounts.value[key].zone_in = 0
                }
            }

            // Update storeTotalCounts for Combined Entrance Exit Zones (by key, under
            // the zone's current name).
            storeTotalCounts.value = storeCounters
                ? storeCountersByCardName(detectionZoneNames.value, storeCounters)
                : {}
        }
        
        const getColorForZone = (zoneType) => {
            return detection_zone_colors_by_name_solid[zoneType] || 'black'
        }

        // -------------------------------------------------------------------
        // Geometry-aware helpers — drive which card variant shows up per zone.
        // -------------------------------------------------------------------

        // Entrance / Exit / Combined EE: dwell concepts don't apply, regardless of
        // whether the zone happens to carry a (legacy) polygon.
        const ENTRANCE_LIKE_TYPES = ['Entrance Zone', 'Exit Zone', 'Combined Entrance Exit Zone']

        const zoneHasPolygonByName = (zoneName) => {
            const z = detectionZoneNames.value?.[zoneName]
            return Array.isArray(z?.points) && z.points.length >= 3
        }
        const zoneHasTripwireByName = (zoneName) => {
            const z = detectionZoneNames.value?.[zoneName]
            return !!(z?.tripwire && Array.isArray(z.tripwire.points) && z.tripwire.points.length === 2)
        }

        // -------------------------------------------------------------------
        // Row partitioning — each "Visits / Crossings / Avg dwell" section gets
        // its own filtered list. A polygon+tripwire zone naturally appears in
        // both the Visits row (via polygon zone_in) and the Crossings row (via
        // tripwire store_in/store_out), so the user sees both signals side by
        // side without duplicating columns inside a single card.
        // -------------------------------------------------------------------

        // Visits row: polygon-bearing zones, excluding Combined EE (whose whole
        // purpose is directional and which always lives in the Crossings row).
        // All four zone lists on this page (here and the Live Activity grid in
        // FlowOutput) sort by zone name. Without it each list inherits whatever
        // order its own source happened to produce — these come from the API
        // response's key order, Live Activity from the DB's row order — so the
        // same zones appeared in different positions in each section and you
        // could not read across them.
        const byZoneName = (a, b) => a[0].localeCompare(b[0])

        const polygonTotalZones = computed(() =>
            Object.entries(zoneTotalCounts.value).filter(([zoneName, z]) => {
                if (z.zone_type === 'Cash Register Zone') return false
                if (z.zone_type === 'Combined Entrance Exit Zone') return false
                return zoneHasPolygonByName(zoneName)
            }).sort(byZoneName)
        )

        // Crossings row: Combined EE always; any other zone with a tripwire.
        const directionalTotalZones = computed(() =>
            Object.entries(zoneTotalCounts.value).filter(([zoneName, z]) => {
                if (z.zone_type === 'Cash Register Zone') return false
                if (z.zone_type === 'Combined Entrance Exit Zone') return true
                return zoneHasTripwireByName(zoneName)
            }).sort(byZoneName)
        )

        // Avg dwell row: only polygon-bearing zones whose dwell is meaningful.
        // Entrance / Exit / Combined EE are about flow, not lingering — skipped.
        const dwellAverageZones = computed(() =>
            Object.entries(zoneAverageTime.value).filter(([zoneName, z]) => {
                if (z.zone_type === 'Cash Register Zone') return false
                if (ENTRANCE_LIKE_TYPES.includes(z.zone_type)) return false
                return zoneHasPolygonByName(zoneName)
            }).sort(byZoneName)
        )

        // Context-aware pill labels. The DB event types are always store_in / store_out;
        // the labels just reflect what those events MEAN for the zone's type.
        const inLabel = (zoneType) => {
            if (zoneType === 'Combined Entrance Exit Zone') return t('explore.counters.pill.inStore')
            if (zoneType === 'Entrance Zone') return t('explore.counters.pill.entries')
            if (zoneType === 'Exit Zone') return t('explore.counters.pill.exits')
            return t('explore.counters.pill.inbound')
        }
        const outLabel = (zoneType) => {
            if (zoneType === 'Combined Entrance Exit Zone') return t('explore.counters.pill.outStore')
            if (zoneType === 'Entrance Zone') return t('explore.counters.pill.wrongWay')
            if (zoneType === 'Exit Zone') return t('explore.counters.pill.wrongWay')
            return t('explore.counters.pill.outbound')
        }

        // Duration sentinels:
        //   1, 7, 30, 365         -> actual day count
        //   1000/2000/3000/4000   -> named ranges (this/last week/month)
        // Map every value to its display label; for unknown plain-number
        // durations fall back to "Last N Days".
        const DURATION_KEYS = {
            1: 'yesterday',
            7: 'last7',
            30: 'last30',
            90: 'last90',
            365: 'last365',
            1000: 'thisWeek',
            2000: 'thisMonth',
            3000: 'lastWeek',
            4000: 'lastMonth',
        }

        const durationText = computed(() => {
            if (isTodaySelected.value) {
                return t('explore.period.today')
            }
            if (localDateRangeMode.value === 'dateRange') {
                // English keeps its en-US dates ("Oct 17, 2026"); Dutch is "17 okt 2026".
                const fmtDate = (d) => currentLanguage() === 'en'
                    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                    : formatDate(new Date(d), { style: 'medium' })
                return t('explore.period.fromTo', { start: fmtDate(startDate.value), end: fmtDate(endDate.value) })
            }
            // localDateRangeMode === 'duration'
            const key = DURATION_KEYS[duration.value]
            return key ? t(`explore.period.${key}`) : t('explore.period.lastNDays', { n: duration.value })
        })

        const formattedAverageDurationTime = (seconds) => {
            if (seconds < 60) {
                return `${seconds.toFixed(0)} sec`
            } else if (seconds < 3600) {
                const minutes = Math.floor(seconds / 60)
                const remainingSeconds = seconds % 60
                return `${minutes} min ${remainingSeconds.toFixed(0)} sec`
            } else {
                const hours = Math.floor(seconds / 3600)
                const remainingMinutes = Math.floor((seconds % 3600) / 60)
                const remainingSeconds = seconds % 60
                const hourWord = currentLanguage() === 'nl' ? 'uur' : 'h'
                return `${hours} ${hourWord} ${remainingMinutes} min ${remainingSeconds.toFixed(0)} sec`
            }
        }

        onUnmounted(() => {
            isMounted.value = false

            stopInterval(); // Stop the interval
            if (currentFetchController && !currentFetchController.signal.aborted) {
                currentFetchController.abort()
            }
        })

        return {
            typeChangedNote,
            flowStore,
            getColorForZone,
            zoneAverageTime,
            zoneTotalCounts,
            storeTotalCounts,
            isLoading,
            durationText,
            formattedAverageDurationTime,
            polygonTotalZones,
            directionalTotalZones,
            dwellAverageZones,
            zoneHasPolygonByName,
            zoneHasTripwireByName,
            zoneTypeLabel,
            inLabel,
            outLabel,
        }
    
    }
}

</script>

<style scoped>
/* -------------------------------------------------------------------
   Sections — each row groups one metric type for all zones, separated
   by a labeled header and a subtle row tint so the eye can scan one
   metric across zones in a single horizontal sweep.
   ------------------------------------------------------------------- */
.kpi-section{
  border-radius:14px;
  padding:12px 14px 14px;
  margin-bottom:18px;
}
.kpi-section.tone-visits     { background: rgba(79, 126, 179, .045) }
.kpi-section.tone-crossings  { background: rgba(178, 193, 73, .055) }
.kpi-section.tone-dwell      { background: rgba(127, 89, 178, .045) }

.section-head{
  display:flex; align-items:center; gap:.5rem;
  margin:0 0 10px 4px;
  font-size:.82rem; font-weight:600; letter-spacing:.02em;
  color:#52606b; text-transform:uppercase;
}
.section-icon{ font-size:.95rem; color:#7a8893 }

/* -------------------------------------------------------------------
   Tighter card grid — Option B tunes the minmax floor down so 4–5
   compact cards fit per row on a typical laptop instead of 2–3.
   ------------------------------------------------------------------- */
.kpi-grid{
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  align-items: stretch;
  width: 100%;
}

.kpi-card{
  background:#fff;
  border:none;
  border-radius:10px;
  box-shadow:0 2px 6px rgba(13,38,76,.05);
  padding:10px 12px 10px 14px;
  display:flex; flex-direction:column;
  gap:6px;
  min-height:88px;
  position:relative;
}

.kpi-card.kpi-accent::before{
  content:"";
  position:absolute;
  inset:0 auto 0 0;
  width:4px;
  background: var(--accent);
  border-radius:10px 0 0 10px;
  opacity:.95;
}

/* 6.6: changed type today (calm blue) */
.kpi-type-note { font-size: 12px; color: #2C5F86; margin: -2px 0 6px; }
.kpi-head{
  display:flex; flex-direction:column; gap:1px;
  padding-bottom:4px;
  border-bottom:1px solid #eef2f6;
}
.kpi-title{
  margin:0;
  font-size:.88rem; font-weight:500; line-height:1.15;
  color:#2b3a44;
  display:flex; align-items:center; gap:.35rem;
}
.kpi-glyph{ font-size:.78rem; color:#6b7a86; opacity:.8 }
.kpi-sub{ font-size:.68rem; color:#81909a; font-weight:600; letter-spacing:.02em }

/* Single-number value */
.kpi-value{ display:flex; align-items:center; min-height:28px; padding-top:2px }
.kpi-number{ font-size: clamp(16px, 1.9vw, 22px); font-weight:700; color:#2b3a44 }

/* Directional in/out duo */
.kpi-duo{ display:grid; grid-template-columns:1fr 1fr; gap:6px; padding-top:2px }
.kpi-pill{
  border:1px solid #e6edf3; border-radius:8px; padding:5px 7px;
  background:#f9fbfd;
  display:flex; flex-direction:column; gap:1px;
  min-width:0;
}
.kpi-pill .label{ font-size:.68rem; color:#6b7a86; font-weight:500 }
.kpi-pill .val{ font-size: clamp(14px, 1.6vw, 18px); font-weight:600; color:#2b3a44 }

.no-zones-container{
  display:flex; justify-content:center; align-items:center; text-align:center;
  width:100%; color:#c1c9cf; margin:16px 0;
}

.loader{
  width:18px; height:18px; padding:2px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
.loader.tiny{ width:16px; height:16px }
@keyframes spinner{ to{ transform:rotate(1turn) } }

@media (max-width: 600px){
  .kpi-grid{ grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) }
}
</style>